import { Injectable, Optional, Inject } from '@angular/core';
import { Observable, concat, of } from 'rxjs';
import { toArray, map, catchError, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MONACO_CODE_EDITOR_CONFIG, MonacoCodeEditorConfig } from '../config';
import { DtsManifest } from "../../code-editor-core/ts/editor";
import { DEFAULT_LIBS_URL, DEFAULT_DTS_MAP_MANIFEST } from './constants';

@Injectable()
export class TsPackageLoaderService {

  private dtsManifest: DtsManifest;

  constructor(
    private httpClient: HttpClient,
    @Optional() @Inject(MONACO_CODE_EDITOR_CONFIG) private config?: MonacoCodeEditorConfig
  ) { }

  /** 获取第三方npm包声明文件的文件夹的地址 */
  private getLibsUrl(): string {
    if (this.config && this.config.tsConfig && this.config.tsConfig.libsUrl) {
      return this.config.tsConfig.libsUrl;
    }
    return DEFAULT_LIBS_URL;
  }

  /**
   * 获取全局设置中的dts包名映射
   * @remarks 全局设置中的dts映射的优先级高于清单文件中的优先级
   * @param imports 从清单文件中读取到的dts包名映射
   */
  private setLibsMap(imports: any): any {
    if (!this.config || !this.config.tsConfig || !this.config.tsConfig.libsMap) {
      return;
    }
    const map = this.config.tsConfig.libsMap;
    for (const key in map) {
      const value = map[key];
      if (!!value && typeof value === 'string') {
        imports[key] = value;
      }
    }
    return imports;
  }

  public async getDtsManifest(): Promise<DtsManifest> {
    if (this.dtsManifest) {
      return this.dtsManifest;
    }
    const basicUrl = this.getLibsUrl();
    const dtsMapFileName = this.config && this.config.tsConfig && this.config.tsConfig.dtsMapManifestFileName || DEFAULT_DTS_MAP_MANIFEST;
    const url = `${basicUrl}/${dtsMapFileName}?version=${new Date().getTime()}`;
    const manifest = await this.httpClient.get(url).pipe(
      catchError(() => {
        return of(null);
      })
    ).pipe(take(1)).toPromise().catch(() => null);
    if (!manifest) {
      this.dtsManifest = {
        imports: this.setLibsMap({}),
        dependencies: {},
      };
      return this.dtsManifest;
    }
    const imports = manifest.imports || {};
    const dependencies = manifest.dependencies || {};
    this.setLibsMap(imports);
    this.dtsManifest = { imports, dependencies };
    return this.dtsManifest;
  }

  /**
   * 加载外部npm定义包
   * @param packages 待加载的包名数组
   * @returns 加载结果
   */
  public async load(packages: string[]): Promise<{ name: string, content: string }[]> {
    if (!packages || packages.length === 0) {
      return Promise.resolve([]);
    }
    const dtsImports = (await this.getDtsManifest()).imports;
    let obsArr: Observable<any>[] = [];
    for (const pkgName of packages) {
      const filename = dtsImports[pkgName];
      if (!filename) {
        continue;
      }
      const basicUrl = this.getLibsUrl();
      const url = `${basicUrl}/${filename}.d.ts?version=${new Date().getTime()}`;
      const headers: HttpHeaders = new HttpHeaders({
        'Content-Type': 'text/plain;charset=UTF-8'
      });
      const options = { headers, responseType: 'text' as 'json' };
      const obs = this.httpClient.get(url, options).pipe(
        map((content: string) => {
          return { name: pkgName, content };
        }),
        catchError(() => {
          return of(null);
        })
      );
      obsArr.push(obs);
    }
    const libContentArrObs = concat(...obsArr).pipe( toArray() );
    return libContentArrObs.toPromise();
  }

}
